import * as Sentry from "@sentry/react";

/**
 * Auth-flow failure reporter.
 *
 * 5xx / network-level failures become Sentry events tagged with the auth step
 * and the backend's `x-request-id` (readable thanks to cy-sso's CORS
 * exposedHeaders) so one search in Sentry finds both the FE and BE event for
 * the same request. 4xx responses (wrong OTP, unknown email, bad password) are
 * user events, not errors — they only leave a breadcrumb on the trail of
 * whatever is captured next. So is a refresh that cy-sso answers with 200 and
 * no valid tokens (`refreshAuthFailed`): the session is gone, the student is
 * signed out, and sessionExpired already logs the end of the session.
 *
 * Never pass otp/password values inside `extra`.
 */
export const logAuthError = (step, err, extra = {}) => {
  try {
    const status = err?.response?.status;
    const requestId = err?.response?.headers?.["x-request-id"] || "none";
    const data = {
      authStep: step,
      status,
      requestId,
      responseMsg: err?.response?.data?.msg || err?.response?.data?.message,
      ...extra,
    };

    // A lost refresh race is expected: a sibling tab or app refreshed first.
    if (err?.refreshRaceLost === true) {
      Sentry.addBreadcrumb({
        category: "auth",
        level: "info",
        message: `auth ${step} failed (refresh race lost)`,
        data,
      });
      return;
    }

    const sessionEnded = err?.refreshAuthFailed === true;
    if (sessionEnded || (status && status < 500)) {
      Sentry.addBreadcrumb({
        category: "auth",
        level: "info",
        message: `auth ${step} failed (${sessionEnded ? "session ended" : status})`,
        data,
      });
      return;
    }

    Sentry.captureException(err, {
      tags: { flow: "auth", authStep: step, request_id: requestId },
      extra: data,
    });
  } catch (_) {
    // Reporting must never break the auth flow.
  }
};

export default logAuthError;
